import { useEffect, useMemo } from "react";
// import { FreyrLibraryProvider } from "@freyr/freyr-react-common";
import { Outlet } from "react-router-dom";
import {
  ThemeProvider,
  createTheme
} from "@mui/material/styles";
import { Box, Stack } from "@mui/material";
import * as coreLocales from "@mui/material/locale";
import * as gridLocales from "@mui/x-data-grid/locales";
import * as pickersLocales from "@mui/x-date-pickers/locales";
import { useGlobalData } from "../hooks/useGlobalData";
import { LANGUAGEMAP } from "../constants";
import { useConfigStore } from "../store/config";
import "../assets/scss/page/index.scss";
import { useUrfReceiveEvents } from "../hooks/urfEvents";
import SideMenu from "../component/SideMenu";
import Loading from "../component/Loading";
import { postRelatedResource } from "../api/uam";
import usePermissionStore from "../store/permission";

type SupportedLocales = keyof typeof coreLocales;
type GridLocales = keyof typeof gridLocales;
type PickersLocales = keyof typeof pickersLocales;

const Layouts = () => {
  const { userId } = useGlobalData();
  const { language } = useConfigStore();
  const themeWithLocale = useMemo(() => {
    const locale: SupportedLocales = LANGUAGEMAP.mui[language];
    const gridLocale: GridLocales = LANGUAGEMAP.mui[language];
    const pickersLocale: PickersLocales = LANGUAGEMAP.mui[language];
    return createTheme(
      {
        palette: {},
      },
      coreLocales[locale],
      gridLocales[gridLocale],
      pickersLocales[pickersLocale]
    );
  }, [language]);

  const {
    isReady,
    setLoading,
    setPermissions,
    setReady,
    resetPermissions,
  } = usePermissionStore((state) => ({
    isReady: state.isReady,
    setLoading: state.setLoading,
    setPermissions: state.setPermissions,
    setReady: state.setReady,
    resetPermissions: state.resetPermissions,
  }));

  useEffect(() => {
    let active = true;

    if (!userId) {
      resetPermissions();
      setReady(true);
      return;
    }

    const loadPermissions = async () => {
      setReady(false);
      setLoading(true);
      setPermissions([]);
      try {
        const response = await postRelatedResource(userId);
        if (!active) return;
        const data = response?.data ?? [];
        const names = data
          .map((item) => item.name)
          .filter((name): name is string => Boolean(name));
        setPermissions(names);
      } catch (error) {
        console.error("Failed to load permissions", error);
      } finally {
        if (active) {
          setLoading(false);
          setReady(true);
        }
      }
    };

    loadPermissions();

    return () => {
      active = false;
    };
  }, [userId, resetPermissions, setLoading, setPermissions, setReady]);

  useUrfReceiveEvents();

  if (!isReady) {
    return (
      <ThemeProvider theme={themeWithLocale}>
        <Loading open />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={themeWithLocale}>
      <Box
        width="100%"
        height="100%"
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        <Stack
          flex={1}
          direction="row"
          width="100%"
          maxWidth="100%"
          overflow="hidden"
          justifyContent="space-between"
          pl={0.5}
          pt={0.2}
          pr={0.2}
        >
          <Box flex={1} maxWidth="100%" overflow="hidden" className="custom-page">
            <Outlet />
          </Box>
        </Stack>
      </Box>
    </ThemeProvider>
  );
};

export default Layouts;